import type { PtoRdIngestWarning, PtoRdRevision } from '@shared/api';

/** Предупреждение о полноте индексации ревизии РД (AERO A2) для баннера. */
export interface RevisionFlagNote {
  /** Ключ для списка: код флага. */
  key: string;
  tone: 'danger' | 'warning';
  /** Что случилось — одной строкой. */
  title: string;
  /** Что это значит и что делать. */
  hint: string;
  /** Подробности по файлам («kj1.pdf: Таблицы файла не разобраны: …»). */
  details: string[];
}

type FlagsOf = Pick<
  PtoRdRevision,
  | 'ingest_status'
  | 'error_message'
  | 'ocr_truncated'
  | 'spec_tables_failed'
  | 'positions_not_replaced'
  | 'ingest_warnings'
> & { metadata?: PtoRdRevision['metadata'] | null };

const REINDEX_HINT =
  'Переиндексируйте ревизию («Переиндексировать» в списке ревизий проекта), когда очередь ' +
  'обработки РД свободна.';

/** Предупреждения ревизии с кодом `code` — строками «файл: текст». */
function detailsOf(warnings: PtoRdIngestWarning[], code: string): string[] {
  return warnings
    .filter((w) => w.code === code)
    .map((w) => (w.file ? `${w.file}: ${w.message}` : w.message));
}

/** Повторы листов в PDF ревизии (`metadata.duplicate_pages`, индексация и
 *  «Обновить разделы»: одинаковый отпечаток рендера листов файла) — строками
 *  «файл: стр. 9 повторяет стр. 8 (лист С701)». */
function repeatedSheets(metadata: unknown): string[] {
  const m = metadata && typeof metadata === 'object' ? (metadata as Record<string, unknown>) : null;
  const list = Array.isArray(m?.duplicate_pages) ? (m.duplicate_pages as unknown[]) : [];
  const out: string[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const d = item as { file?: unknown; page?: unknown; of_page?: unknown; sheet_code?: unknown };
    if (typeof d.page !== 'number' || typeof d.of_page !== 'number') continue;
    const sheet = typeof d.sheet_code === 'string' && d.sheet_code ? ` (лист ${d.sheet_code})` : '';
    const text = `стр. ${d.page} повторяет стр. ${d.of_page}${sheet}`;
    out.push(typeof d.file === 'string' && d.file ? `${d.file}: ${text}` : text);
  }
  return out;
}

/**
 * Предупреждения о полноте последней индексации ревизии: индексация не
 * завершена, табличные позиции не заменены, OCR обрезан, разбор таблиц упал,
 * основные надписи листов не прочитаны, лист в PDF повторён, эмбеддинги не
 * посчитаны. Полная индексация — пустой список. Ревизия без флагов (API до
 * выпуска 3) — тоже пустой список.
 */
export function revisionFlagNotes(r: FlagsOf): RevisionFlagNote[] {
  const warnings = Array.isArray(r.ingest_warnings) ? r.ingest_warnings : [];
  const out: RevisionFlagNote[] = [];
  if (r.ingest_status === 'failed') {
    out.push({
      key: 'ingest_failed',
      tone: 'danger',
      title: 'Индексация не завершена — табличные позиции не заменены',
      hint: `${r.error_message ? `${r.error_message}. ` : ''}${REINDEX_HINT}`,
      details: [],
    });
  } else if (r.positions_not_replaced === true) {
    out.push({
      key: 'positions_not_replaced',
      tone: 'danger',
      title: 'Позиции не заменены — в «Объёмах» табличные позиции прежней индексации',
      hint:
        'Последняя индексация разобрала таблицы не полностью, поэтому прежние строки таблиц ' +
        'сохранены, а извлечение ИИ не запускалось. ' +
        REINDEX_HINT,
      details: detailsOf(warnings, 'positions_not_replaced'),
    });
  }
  if (r.ocr_truncated === true) {
    out.push({
      key: 'ocr_truncated',
      tone: 'warning',
      title: 'OCR обрезан — часть листов не распознана',
      hint:
        'Распознавание листов без текстового слоя (сканы, чертежи со штрихованными надписями) ' +
        'прервано по лимиту времени: строк этих листов в таблицах нет. ' +
        REINDEX_HINT,
      details: detailsOf(warnings, 'ocr_truncated'),
    });
  }
  if (r.spec_tables_failed === true) {
    out.push({
      key: 'spec_tables_failed',
      tone: 'warning',
      title: 'Разбор таблиц упал — часть файлов не разобрана',
      hint: `Таблицы спецификаций и ВОР этих файлов не прочитаны. ${REINDEX_HINT}`,
      details: detailsOf(warnings, 'spec_failed'),
    });
  }
  const sheets = detailsOf(warnings, 'sheets_failed');
  if (sheets.length > 0) {
    out.push({
      key: 'sheets_failed',
      tone: 'warning',
      title: 'Основные надписи листов не прочитаны — «Раздел» без кода части',
      hint:
        'Код части РД с основной надписи листа (СП01, ВР01…) по этим файлам не прочитан: ' +
        'у строк — часть по виду таблицы или по прежней индексации, номера строк — по ней. ' +
        REINDEX_HINT,
      details: sheets,
    });
  }
  const repeats = repeatedSheets(r.metadata);
  if (repeats.length > 0) {
    out.push({
      key: 'duplicate_pages',
      tone: 'warning',
      title: 'Повтор листа в PDF — повторная страница не считается',
      hint:
        'Страница файла повторяет другую его страницу (ошибка сборки комплекта). Позиции ' +
        'повторной страницы — вне сумм и списков ведомости, видны в «Сверке». Запросите у ' +
        'проектировщика исправленный файл — «В запрос SQ» на экране «Объёмы».',
      details: repeats,
    });
  }
  const embed = detailsOf(warnings, 'embed_failed');
  if (embed.length > 0) {
    out.push({
      key: 'embed_failed',
      tone: 'warning',
      title: 'Поиск по РД — только по словам',
      hint: 'Эмбеддинги фрагментов не посчитаны: поиск по смыслу в чате с РД для этой ревизии не работает.',
      details: embed,
    });
  }
  return out;
}
